import { backend_source, message_page_limit } from "./bolide_assistant_constants";

export default {
  async fetch_backend_message_page(conversation_id, params = {}) {
    return this.fetch_assist_history({
      chat_id: conversation_id,
      limit: message_page_limit,
      ...params,
    });
  },

  async ensure_active_conversation_messages(conversation_id = this.active_conversation_id) {
    const conversation = this.get_conversation_by_id(conversation_id);
    if (!conversation || conversation.source !== backend_source) return false;
    if (conversation.messages_loaded || conversation.is_loading_messages) return false;
    try {
      return await this.load_conversation_messages(conversation_id, { replace: true });
    } catch {
      return false;
    }
  },

  async load_conversation_messages(conversation_id, options = {}) {
    const conversation = this.get_conversation_by_id(conversation_id);
    if (!conversation || conversation.is_loading_messages) return false;

    conversation.is_loading_messages = true;
    try {
      const page = await this.fetch_backend_message_page(conversation_id, {
        cursor: options.cursor || null,
      });
      const page_messages = this.history_records_to_messages(page.results);
      conversation.messages = options.prepend
        ? this.merge_message_pages(page_messages, conversation.messages)
        : page_messages;
      conversation.messages_loaded = true;
      conversation.messages_next_cursor = page.next_cursor || null;
      conversation.message_count = Math.max(
        conversation.message_count || 0,
        Math.ceil(conversation.messages.length / 2)
      );
      conversation.preview = this.get_messages_preview(conversation.messages)
        || conversation.preview;
      if (!options.prepend) this.scroll_to_latest_message({ settle: true });
      return true;
    } finally {
      conversation.is_loading_messages = false;
    }
  },

  async load_older_active_messages() {
    const conversation = this.active_conversation;
    if (!conversation?.messages_next_cursor || conversation.is_loading_messages) return false;
    const scroll_state = this.$refs.chat_messages?.capture_scroll_state?.();
    try {
      const loaded = await this.load_conversation_messages(conversation.id, {
        cursor: conversation.messages_next_cursor,
        prepend: true,
      });
      if (loaded && scroll_state) {
        this.$refs.chat_messages?.restore_scroll_state?.(scroll_state);
      }
      return loaded;
    } catch {
      return false;
    }
  },

  merge_message_pages(incoming_messages, existing_messages) {
    const existing_ids = new Set(existing_messages.map((message) => message.id));
    return [
      ...incoming_messages.filter((message) => !existing_ids.has(message.id)),
      ...existing_messages,
    ];
  },

  history_records_to_messages(records) {
    if (!Array.isArray(records)) return [];
    return [...records]
      .sort((left, right) => (
        this.get_timestamp_sort_value(left.created_at)
        - this.get_timestamp_sort_value(right.created_at)
      ))
      .flatMap((record) => this.history_record_messages(record));
  },

  history_record_messages(record) {
    const record_id = this.get_history_record_id(record);
    const messages = [
      this.create_message("user", record.query_text || "", {
        id: `${record_id}-user`,
        created_at: record.created_at,
      }),
    ];
    const assistant_text = this.get_history_record_assistant_text(record);
    if (assistant_text) {
      messages.push(
        this.create_message("assistant", assistant_text, {
          id: `${record_id}-assistant`,
          created_at: record.completed_at || record.created_at,
        })
      );
    }
    return messages;
  },

  get_history_record_assistant_text(record) {
    if (record.response_text) return record.response_text;
    const status = String(record.status || "").toUpperCase();
    if (status === "FAILED") {
      return record.error_message || "AI Campus VSI could not process that request.";
    }
    if (["QUEUED", "PROCESSING"].includes(status)) {
      return "AI Campus VSI is still working on this request.";
    }
    return "";
  },

  get_history_record_id(record) {
    const stable_id = record.id || `${record.chat_id || "default"}-${record.created_at}`;
    return `history-${stable_id}`;
  },

  get_timestamp_sort_value(value) {
    const timestamp = Date.parse(value || "");
    return Number.isNaN(timestamp) ? 0 : timestamp;
  },

  get_messages_preview(messages) {
    const last_message = [...(messages || [])].reverse().find((message) => (
      String(message?.text || "").trim()
    ));
    return last_message?.text || "";
  },
};
